import { SelectUsers } from '@nile/mangifera/src/Pebbles/ChatContainer';
import { Badge, Button, DisplayText, Tippy } from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import {
  getTranslatedText,
  withErrorBoundary,
  SearchableEmailSelect
} from '@nile/components';
import TippyJS from '@tippyjs/react';
import { FormattedMessage, useIntl } from 'react-intl';

interface Recipient {
  userId: string;
  name: string;
  email: string;
}

interface UserConfigProps {
  property: string;
  query: any;
  params: {
    client: any;
    variables: any;
    [key: string]: any;
  };
  methodName: string;
  isMulti?: boolean;
  creatable?: boolean;
}

interface RecipientsProps {
  to: {
    value: Recipient;
    config: UserConfigProps;
    isMailList?: boolean;
    onChange: (value: Recipient) => void;
  };
  cc: {
    value: Recipient[];
    config: UserConfigProps;
    isMailList?: boolean;
    onChange: (value: Recipient[]) => void;
  };
  bcc: {
    value: Recipient[];
    config: UserConfigProps;
    isMailList?: boolean;
    onChange: (value: Recipient[]) => void;
  };
  hasAdditionalRecipients: boolean;
  conversationThreads: any[] | null;
}

const Label = ({ label }) => {
  return (
    <DisplayText element="p" size="large">
      {label}:
    </DisplayText>
  );
};

const RecipientsComponent = (props) => {
  const data = props?.data;
  const config = data?.config;
  const [value, setValue] = useState(data?.value);
  const onChange = data?.onChange;
  const isMailList = data?.isMailList;

  const handleChange = (val) => {
    setValue(val);
    onChange(val);
  };

  if (isMailList) {
    return (
      <div className="w100">
        <SearchableEmailSelect
          mailList={value}
          label={config?.label}
          updateChange={handleChange}
          placeholder={config?.placeholder}
          noBorder
        />
      </div>
    );
  }

  return (
    <div className="w100">
      <SelectUsers
        {...config}
        value={value}
        noMargin
        updateField={handleChange}
      />
    </div>
  );
};

const DisplayUserData = (props) => {
  const { value, showOnlyEmail, showOnlyName } = props;
  const getUserData = () => {
    if (isEmpty(value)) {
      return null;
    }

    if (Array.isArray(value)) {
      const [user, ...rest] = value;

      return {
        user,
        otherUsers: rest.length > 0 ? rest : null
      };
    }

    return {
      user: value,
      otherUsers: null
    };
  };
  const formattedUserData = getUserData();
  const userInfo = formattedUserData?.user;
  const otherUsers = formattedUserData?.otherUsers || null;
  const count = otherUsers?.length || null;

  const getTippyContent = () => {
    const name = userInfo?.name || userInfo?.email;
    const email = userInfo?.email;

    if (showOnlyName) {
      return name;
    }

    if (showOnlyEmail || !userInfo?.name) {
      return email;
    }

    return `${name} (${email})`;
  };

  const getOtherUsersTippyContent = () => {
    if (!Array.isArray(otherUsers) || otherUsers.length === 0) {
      return null;
    }

    return (
      <div className="flex col tw-gap-y-1 tw-p-2">
        {otherUsers.map((user, index) => {
          const name = user?.name || user?.email;
          const email = user?.email;

          const getContent = () => {
            if (showOnlyEmail) {
              return email;
            }
            if (showOnlyName || !user?.name) {
              return name;
            }

            return `${name} (${email})`;
          };

          const content = getContent();

          return (
            <div key={content || index} className="tippy-row">
              {content}
            </div>
          );
        })}
      </div>
    );
  };

  const tippyContent = getTippyContent();
  const otherUsersTippyContent = getOtherUsersTippyContent();
  return (
    <div className="flex actr tw-gap-x-2 fx-sb ellipsis w100">
      <div className="ellipsis">
        <Tippy
          title={tippyContent && tippyContent}
          arrow
          interactive
          key={tippyContent}
        >
          <div className="ellipsis pointer">
            <DisplayText element="span" size="large" weight="SemiBold">
              {tippyContent}
            </DisplayText>
          </div>
        </Tippy>
      </div>
      {count && count > 0 && (
        <TippyJS arrow interactive html content={otherUsersTippyContent}>
          <div className="pointer">
            <Badge>
              +{count} <FormattedMessage id="app.common.more" />
            </Badge>
          </div>
        </TippyJS>
      )}
    </div>
  );
};

const RecipientsConfig = (props: RecipientsProps) => {
  const {
    to,
    cc,
    bcc,
    hasAdditionalRecipients = false,
    conversationThreads
  } = props;

  const intl = useIntl();

  const defaultShowCC = !isEmpty(cc?.value);
  const defaultShowBCC = !isEmpty(bcc?.value);
  const [showCC, setShowCC] = useState(defaultShowCC);
  const [showBCC, setShowBCC] = useState(defaultShowBCC);

  return (
    <div id="global-chat-recipients-config">
      <div className="flex actr fx-sb tw-gap-x-2 w100">
        <div className="ellipsis w100">
          <div className="flex tw-gap-x-2 w100">
            <div className={to?.config ? 'tw-mt-2' : ''}>
              <Label
                label={getTranslatedText(intl, 'ticket.details.chat.to')}
              />
            </div>
            {to?.config ? (
              <RecipientsComponent data={to} />
            ) : (
              <DisplayUserData
                value={to?.value}
                showOnlyEmail={to?.showOnlyEmail || false}
                showOnlyName={to?.showOnlyName || false}
              />
            )}
          </div>
        </div>

        <div className="flex tw-gap-x-1">
          <Button
            plainSecondary
            onClick={() => setShowCC((prevState) => !prevState)}
          >
            {getTranslatedText(intl, 'ticket.details.recepient.cc')}
          </Button>
          <Button
            plainSecondary
            onClick={() => setShowBCC((prevState) => !prevState)}
          >
            {getTranslatedText(intl, 'ticket.details.recepient.bcc')}
          </Button>
        </div>
      </div>

      <div
        className={`tw-gap-x-2 tw-mt-2 ${showCC ? 'flex' : 'hiddenActions'}`}
      >
        <div className="tw-mt-2">
          <Label
            label={getTranslatedText(intl, 'ticket.details.recepient.cc')}
          />
        </div>
        <RecipientsComponent data={cc} />
      </div>

      <div
        className={`tw-gap-x-2 tw-mt-2 ${showBCC ? 'flex' : 'hiddenActions'}`}
      >
        <div className="tw-mt-2">
          <Label
            label={getTranslatedText(intl, 'ticket.details.recepient.bcc')}
          />
        </div>
        <RecipientsComponent data={bcc} />
      </div>
    </div>
  );
};

export default withErrorBoundary(RecipientsConfig);
